import React, { useState } from "react";
import { Siren, Loader2, Camera } from "lucide-react";
import { maintenanceApi } from "@/services/api";
import { apiError } from "@/lib/format";
import { PhotoCapture } from "@/components/PhotoCapture";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export function EmergencyCompleteDialog({ jobId, open, onOpenChange, onDone }) {
  const [before, setBefore] = useState([]);
  const [after, setAfter] = useState([]);
  const [notes, setNotes] = useState("");
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setBefore([]); setAfter([]); setNotes(""); setCost("");
  }, [open]);

  const submit = async () => {
    if (!before[0] || !after[0]) { toast.error("Both a before and an after photo are required."); return; }
    setSaving(true);
    try {
      await maintenanceApi.completeEmergency(jobId, {
        before_photo: before[0],
        after_photo: after[0],
        completion_notes: notes.trim() || undefined,
        actual_cost: cost.trim() ? parseFloat(cost) : undefined,
      });
      toast.success("Emergency job completed");
      onDone();
    } catch (e) { toast.error(apiError(e, "Could not complete job")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="emergency-complete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Siren className="h-5 w-5 text-red-600" /> Complete emergency call-out</DialogTitle>
          <DialogDescription>Before and after photos are required for emergency works.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Camera className="h-4 w-4 text-slate-500" /> Before photo *</Label>
            <PhotoCapture photos={before} onChange={setBefore} max={1} testId="emergency-before" />
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Camera className="h-4 w-4 text-slate-500" /> After photo *</Label>
            <PhotoCapture photos={after} onChange={setAfter} max={1} testId="emergency-after" />
          </div>
          <div className="space-y-1.5">
            <Label>Completion notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was done to make safe…" data-testid="emergency-notes" />
          </div>
          <div className="space-y-1.5">
            <Label>Actual cost (£, optional)</Label>
            <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" data-testid="emergency-cost" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="emergency-submit">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Siren className="mr-2 h-4 w-4" />} Mark complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
